import { useState, useEffect } from 'react';
import { photographerService } from '../../../services/photographerService';
import PhotographerProfileModal from '../../photographer-results/components/PhotographerProfileModal';
import { normalizeExperienceLevelToUi } from '../../../utils/experienceLevel';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const AlternativePhotographersPanel = ({
  booking,
  onSelectPhotographer,
  selectingId,
}) => {
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileModalId, setProfileModalId] = useState(null);

  useEffect(() => {
    loadAlternatives();
  }, []);

  const loadAlternatives = async () => {
    try {
      setLoading(true);
      const currentId = booking.assignedPhotographerId || booking.assigned_photographer_id;
      const city = booking.city || '';
      const photographyType = booking.photographyType || booking.photography_type || booking.serviceType || '';
      const shootingMethods = (() => {
        const raw = booking.shootingMethods || booking.shooting_methods || booking.shootingMethod;
        if (!raw) return [];
        return Array.isArray(raw) ? raw : [raw];
      })();
      const snap = booking.pricingSnapshot || booking.pricing_snapshot || {};
      const experienceLevel =
        snap.experienceLevel ||
        snap.experience_level ||
        booking.experienceLevel ||
        booking.experience_level ||
        '';
      const experienceLevelUi = normalizeExperienceLevelToUi(experienceLevel) || experienceLevel;
      const preciseLocation = (() => {
        const raw = booking.preciseLocation || booking.precise_location;
        if (!raw) return null;
        try {
          const p = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (p?.lat && p?.lng) return { lat: p.lat, lng: p.lng };
        } catch (_) { /* ignore */ }
        return null;
      })();

      const results = await photographerService.fetchAlternativePhotographers({
        city, photographyType, shootingMethods, experienceLevel: experienceLevelUi, preciseLocation,
        excludeIds: currentId ? [currentId] : [],
        bookingDate: booking.bookingDate || booking.booking_date || null,
      });
      setPhotographers(results);
    } catch (err) {
      console.warn('Failed to load alternative photographers:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (photographers.length === 0) {
    return (
      <div className="py-10 text-center text-slate-400">
        <Icon name="UserX" size={44} className="mx-auto mb-3 opacity-30" />
        <p className="text-sm font-medium text-slate-500">لا يوجد مصورون متاحون حالياً في منطقتك</p>
        <p className="text-xs text-slate-400 mt-1">يمكنك استخدام زر البحث التلقائي أعلاه</p>
      </div>
    );
  }

  return (
    <>
      <p className="text-xs text-slate-400 mb-4">
        {photographers.length} مصور متاح · اضغط "اختر" لتعيينه فوراً
      </p>

      <div className="space-y-3">
        {photographers.map(p => (
          <PhotographerCard
            key={p.id}
            photographer={p}
            onViewProfile={() => setProfileModalId(p.id)}
            onSelect={() => onSelectPhotographer(p.id)}
            isSelecting={selectingId === p.id}
            disabled={!!selectingId}
          />
        ))}
      </div>

      <PhotographerProfileModal
        isOpen={!!profileModalId}
        onClose={() => setProfileModalId(null)}
        photographerId={profileModalId}
      />
    </>
  );
};

const PhotographerCard = ({ photographer, onViewProfile, onSelect, isSelecting, disabled }) => {
  const fullStars = Math.round(photographer.rating);

  return (
    <div className={`rounded-2xl border transition-all ${disabled && !isSelecting ? 'opacity-60' : ''} ${isSelecting ? 'border-primary bg-primary/5 shadow-md' : 'border-slate-200 bg-white hover:border-primary/40 hover:shadow-sm'}`}>
      {/* Top row: avatar + name + tags */}
      <div className="flex items-start gap-3 p-4 pb-3">
        {/* Avatar */}
        <div className="flex-shrink-0 w-14 h-14 rounded-xl overflow-hidden border border-slate-100 shadow-sm bg-slate-100">
          <Image
            src={photographer.avatar}
            alt={photographer.name}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          {/* Name + verified */}
          <div className="flex items-center gap-1.5 mb-1">
            <p className="font-bold text-slate-900 text-sm leading-tight truncate">{photographer.name}</p>
            {photographer.isVerified && (
              <Icon name="BadgeCheck" size={14} className="text-primary flex-shrink-0" />
            )}
          </div>

          {/* City + distance + level */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 mb-2">
            <span className="flex items-center gap-0.5">
              <Icon name="MapPin" size={11} className="text-slate-400" />
              {photographer.city}
            </span>
            {photographer.distanceKm !== null && photographer.distanceKm !== undefined && (
              <span className="flex items-center gap-0.5 text-primary font-semibold">
                <Icon name="Navigation" size={11} />
                {photographer.distanceKm} كم
              </span>
            )}
            <span className="flex items-center gap-0.5">
              <Icon name="Briefcase" size={11} className="text-slate-400" />
              {photographer.experienceLevel}
            </span>
          </div>

          {/* Rating */}
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map(s => (
              <Icon
                key={s}
                name="Star"
                size={11}
                className={s <= fullStars ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-200'}
              />
            ))}
            {photographer.rating > 0 && (
              <span className="text-[11px] text-slate-500 mr-0.5 font-medium">{photographer.rating.toFixed(1)}</span>
            )}
            {photographer.reviewCount > 0 && (
              <span className="text-[10px] text-slate-400">({photographer.reviewCount})</span>
            )}
          </div>
        </div>
      </div>

      {/* Specialties row */}
      {photographer.specialties.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 pb-3">
          {photographer.specialties.slice(0, 3).map((s, i) => (
            <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-medium rounded-full">
              {s}
            </span>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 px-4 pb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={onViewProfile}
          disabled={disabled}
          className="flex-1 text-xs border-slate-200 text-slate-600 hover:bg-slate-50"
          iconName="Eye"
          iconPosition="right"
        >
          نبذة عني
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={onSelect}
          loading={isSelecting}
          disabled={disabled}
          className="flex-1 text-xs bg-primary hover:bg-primary/90"
          iconName={isSelecting ? undefined : 'Check'}
          iconPosition="right"
        >
          {isSelecting ? 'جاري التعيين...' : 'اختر هذا المصور'}
        </Button>
      </div>
    </div>
  );
};

export default AlternativePhotographersPanel;
